/**
 * Hook for managing audio input devices
 * Handles device enumeration, selection, and localStorage persistence
 */

import { microphoneConstraints, useMicrophoneAccess } from '@careflux/utils/microphone';
import { useCallback, useEffect, useState } from 'react';

export const SELECTED_MICROPHONE_STORAGE_KEY = 'selectedMicrophoneId';

export function useAudioDevices(enabled = true) {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(() => {
    return localStorage.getItem(SELECTED_MICROPHONE_STORAGE_KEY) || '';
  });
  const microphone = useMicrophoneAccess(enabled);
  const { retry } = microphone;

  const refreshAudioDevices = useCallback(
    async (deviceId?: string) => {
      if (!(await retry(microphoneConstraints(deviceId)))) return false;
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = allDevices.filter((device) => device.kind === 'audioinput');
        setDevices(audioDevices);
        setSelectedDeviceId((current) =>
          audioDevices.some((device) => device.deviceId === current) ? current : audioDevices[0]?.deviceId || ''
        );
        return true;
      } catch {
        // The shared hook exposes capture failures to the inline notice.
        return false;
      }
    },
    [retry]
  );

  // Enumerate audio devices on mount. Gated by `enabled` so callers that
  // aren't currently showing the recording UI (e.g. the embed widget on
  // an error/subscription-gate screen) don't trigger the browser's mic
  // permission prompt. Defaults to true so main-app call sites keep
  // working with no change.
  useEffect(() => {
    if (enabled) void refreshAudioDevices();
  }, [enabled, refreshAudioDevices]);

  const retryMicrophone = useCallback(
    () =>
      refreshAudioDevices(
        devices.some((device) => device.deviceId === selectedDeviceId) ? selectedDeviceId : undefined
      ),
    [refreshAudioDevices, devices, selectedDeviceId]
  );

  // Save selected device to localStorage whenever it changes
  useEffect(() => {
    if (selectedDeviceId) {
      localStorage.setItem(SELECTED_MICROPHONE_STORAGE_KEY, selectedDeviceId);
    }
  }, [selectedDeviceId]);

  return {
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    microphone,
    retryMicrophone,
  };
}
